import { blinkConstants } from '@thoughtspot/blink-constants';
import {
    isOrgPerUrlEnabled,
    isOrgUiEnabled,
} from '@thoughtspot/session-service';
import { Applications } from '../../../routing/routes';
import { routePaths } from '../../../routing/routes-util';
import { removeOverrideOrgId } from '../../../services/system/nav-service/nav-service';

/**
 * Resolves the effective nav theme type, converting 'custom' to 'dark'
 * when the app panel color matches the dark brand color.
 * Single source of truth used by both authenticated-app-layout and nav.container.
 */
export const getComputedNavThemeType = (
    config:
        | {
              getThemeType: () => string;
              getAppPanelColor: (
                  isV3?: boolean,
              ) => { color?: string } | undefined;
          }
        | undefined,
    isNavigationV3Enabled: boolean,
): string | undefined => {
    const themeType = config?.getThemeType();
    if (themeType === blinkConstants.themeType.CUSTOM && config) {
        const appPanelColor = config.getAppPanelColor(isNavigationV3Enabled)
            ?.color;
        if (appPanelColor) {
            const normalizedAppPanelColor = appPanelColor
                .toUpperCase()
                .substring(0, 7);
            const normalizedDarkColor = blinkConstants.themeColor.DARK.toUpperCase().substring(
                0,
                7,
            );
            if (normalizedAppPanelColor === normalizedDarkColor) {
                return blinkConstants.themeType.DARK;
            }
        }
    }
    return themeType;
};

/**
 * Constructs the admin URL with the org override removed when needed
 * (for use with window.open when opening in new tab)
 */
const getAdminUrlWithoutOrgOverride = (): string => {
    const baseUrl = `${window.location.href.split('#')[0]}#/admin`;
    return removeOverrideOrgId(baseUrl);
};

export const goToApplicationHome = (
    applicationID: string,
    navService: { goToUrl: (url: string) => void },
    event: React.MouseEvent,
): void => {
    if (event.metaKey || event.ctrlKey) {
        if (applicationID === Applications.INSIGHTS) {
            window.open('#/insights/home', '_blank');
        } else if (applicationID === Applications.DATA) {
            window.open('#/data/tables', '_blank');
        } else if (applicationID === Applications.DEVELOP) {
            window.open('#/develop', '_blank');
        } else if (applicationID === Applications.ADMIN) {
            // When Per Org URL feature is enabled, remove overrideOrgId from URL
            // when navigating to Admin, as Admin should always operate in the
            // context of the current session org, not the URL override org
            if (isOrgUiEnabled() && isOrgPerUrlEnabled()) {
                window.open(getAdminUrlWithoutOrgOverride(), '_blank');
            } else {
                window.open('#/admin', '_blank');
            }
        } else if (applicationID === Applications.TEAMS_ADMIN) {
            window.open(`#${routePaths.Teams}`, '_blank');
        }
        return;
    }

    if (applicationID === Applications.INSIGHTS) {
        navService.goToUrl('/insights/home');
    } else if (applicationID === Applications.DATA) {
        navService.goToUrl('/data/tables');
    } else if (applicationID === Applications.DEVELOP) {
        navService.goToUrl('/develop');
    } else if (applicationID === Applications.ADMIN) {
        // Navigate via React Router so the navigation blocker can intercept
        // and show the unsaved changes popup if needed. The URL cleanup
        // (removing overrideOrgId for session context reset) is handled in
        // the AdminContainer on mount.
        navService.goToUrl('/admin');
    } else if (applicationID === Applications.TEAMS_ADMIN) {
        navService.goToUrl(routePaths.Teams);
    }
};
